import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found');
      return;
    }

    verifyPayment(sessionId);
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/verify/${sessionId}`);
      
      if (response.data.success && response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage(response.data.message);
        setDetails(response.data);
      } else {
        setStatus('error');
        setMessage(response.data.message || 'Payment verification failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to verify payment');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="glass-panel border-0 max-w-lg w-full">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img 
              src={DENTAL_PULSE_LOGO} 
              alt="DentalPulse"
              className="h-12 w-auto object-contain"
              data-testid="dentalpulse-logo"
            />
          </div>
          <CardTitle className="text-center text-2xl">
            {status === 'verifying' && (
              <div className="flex items-center justify-center gap-3 text-dp-600">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span>Verifying Payment...</span>
              </div>
            )}
            {status === 'success' && (
              <div className="flex items-center justify-center gap-3 text-green-600">
                <CheckCircle2 className="w-8 h-8" />
                <span>Payment Successful!</span>
              </div>
            )}
            {status === 'error' && (
              <div className="flex items-center justify-center gap-3 text-red-600">
                <XCircle className="w-8 h-8" />
                <span>Payment Failed</span>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-slate-600">{message}</p>
          
          {details && details.subscription_tier && (
            <div className="bg-slate-100 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-1">Subscription Tier</p>
              <p className="text-lg font-semibold text-slate-800 capitalize">{details.subscription_tier}</p>
            </div>
          )}

          <div className="flex gap-3">
            {status === 'success' && (
              <>
                <Button
                  onClick={() => navigate('/license')}
                  className="flex-1 bg-dp-600 hover:bg-dp-700 text-white"
                  data-testid="view-license-button"
                >
                  View License Details
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="flex-1"
                  data-testid="go-dashboard-button"
                >
                  Go to Dashboard
                </Button>
              </>
            )}
            {status === 'error' && (
              <>
                <Button
                  onClick={() => navigate('/license')}
                  className="flex-1 bg-slate-800 hover:bg-slate-900 text-white"
                >
                  Back to License Page
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="flex-1"
                >
                  Retry Verification
                </Button>
              </>
            )}
          </div>

          {status === 'success' && (
            <p className="text-xs text-center text-slate-500 mt-4">
              A confirmation email has been sent to your registered email address.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
